import { createContext, useReducer, useCallback } from "react";

import axios from "../../utils/axios/v1/userAxios";

const SET_SETTINGS = "SET_SETTINGS";
const SET_LOADING = "SET_LOADING";
const SET_ERROR = "SET_ERROR";

const initialState = {
  settings: null,
  loading: false,
  errMsg: null,
};

const SessionSettingsReducer = (state, action) => {
  switch (action.type) {
    case SET_SETTINGS:
      return {
        ...state,
        settings: action.payload.settings,
      };
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload.loading,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const SessionSettingsContext = createContext(null);

function SessionSettingsProvider({ children }) {
  const [state, dispatch] = useReducer(SessionSettingsReducer, initialState);

  const setSettings = useCallback((settings) => {
    dispatch({
      type: SET_SETTINGS,
      payload: { settings },
    });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({
      type: SET_LOADING,
      payload: { loading },
    });
  }, []);

  const setErr = useCallback((errMsg) => {
    dispatch({
      type: SET_ERROR,
      payload: { errMsg },
    });
  }, []);

  const getSessionSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/organisation/session-settings");
      setSettings(response.data);
      setErr(null);
      return response.data;
    } catch (err) {
      setSettings(null);
      setErr(err.message || "Failed to fetch session settings");
      return null;
    } finally {
      setLoading(false);
    }
  }, [setSettings, setErr, setLoading]);

  const updateSessionSettings = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await axios.patch("/organisation/session-settings", data);
      setSettings(response.data);
      setErr(null);
      return response.data;
    } catch (err) {
      setErr(err.message || "Failed to update session settings");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setSettings, setErr, setLoading]);

  return (
    <SessionSettingsContext.Provider
      value={{
        ...state,
        getSessionSettings,
        updateSessionSettings,
        setErr,
      }}
    >
      {children}
    </SessionSettingsContext.Provider>
  );
}

export { SessionSettingsContext, SessionSettingsProvider };
